import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { ArrowUp, Paperclip, Square } from "lucide-react";
import { useWorkspace } from "@/shared/ui/WorkspaceContext";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { useAutosizeTextarea } from "@/shared/hooks/use-autosize-textarea";
import { useFileDropTarget } from "@/features/intelligence/hooks/use-file-drop-target";
import { AttachmentChips } from "@/features/intelligence/components/AttachmentChips";
import {
  ATTACHMENT_ACCEPT,
  hasUploadsInFlight,
  type PendingAttachment,
} from "@/features/intelligence/lib/attachments";
import { cn } from "@/shared/cn";
import { ADVICE_PORTAL_NEW, ADVICE_PORTAL_NEW_CLIENT } from "@/shared/ui/dock-pages";
import { Pill } from "@/shared/ui/primitives/Pill";
import {
  COMPOSER_CHIPS,
  type ComposerChipKey,
  chipsThatFit,
} from "@/features/intelligence/lib/composer-chips";

export function SimonBarInput({
  onSubmitText,
  sendLocked,
  attachments,
  onAttach,
  onRemoveAttachment,
  onStop,
}: {
  onSubmitText: (text: string) => void;
  sendLocked: boolean;
  attachments: readonly PendingAttachment[];
  onAttach: (file: File) => void;
  onRemoveAttachment: (key: string) => void;
  onStop?: () => void;
}) {
  const { t } = useTranslation();
  const { showApplication, pendingComposerDraft, consumeComposerDraft } = useWorkspace();

  const [value, setValue] = useState("");
  const [multiline, setMultiline] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const textMeasureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [draftsTaken, setDraftsTaken] = useState(0);
  useEffect(() => {
    if (pendingComposerDraft === null) return;
    consumeComposerDraft();
    setValue(pendingComposerDraft);
    setDraftsTaken((taken) => taken + 1);
  }, [pendingComposerDraft, consumeComposerDraft]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (draftsTaken === 0 || !input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [draftsTaken]);

  useAutosizeTextarea(inputRef, value);

  const measureMultiline = useCallback(() => {
    const prompt = promptRef.current;
    const measure = textMeasureRef.current;
    if (!prompt || !measure) return;
    const forced = value.includes("\n");
    const availableTextWidth = prompt.clientWidth - 44 - 76;
    const wrapped = availableTextWidth > 0 && measure.scrollWidth > availableTextWidth;
    setMultiline(forced || wrapped);
  }, [value]);

  useLayoutEffect(measureMultiline, [measureMultiline]);

  useEffect(() => {
    const prompt = promptRef.current;
    if (!prompt || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureMultiline);
    observer.observe(prompt);
    return () => observer.disconnect();
  }, [measureMultiline]);

  function send() {
    if (sendLocked) return;
    if (hasUploadsInFlight(attachments)) return;
    const trimmed = value.trim();
    if (!trimmed || trimmed === "/" || trimmed === "@") return;
    onSubmitText(trimmed);
    setValue("");
  }

  function onInputKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function onChip(key: ComposerChipKey) {
    showApplication(key === "newClient" ? ADVICE_PORTAL_NEW_CLIENT : ADVICE_PORTAL_NEW, true);
  }

  const trimmed = value.trim();
  const hasText = trimmed !== "" && trimmed !== "/" && trimmed !== "@";
  const uploading = hasUploadsInFlight(attachments);
  const armed = hasText && !uploading;
  const { dropping, dropTarget } = useFileDropTarget(onAttach);
  const fileRef = useRef<HTMLInputElement>(null);

  const shortcutRow =
    value === "" ? <ComposerChips label={(key) => t(key)} onChip={onChip} /> : null;

  const attachmentControl = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        className="sr-only"
        aria-label={t("intelligence.attach")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onAttach(file);
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        aria-label={t("intelligence.attach")}
        title={t("intelligence.attach")}
        className={cn(
          "flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[var(--radius-lg)]",
          "text-[var(--color-text-muted)] transition-colors duration-[var(--duration-fast)]",
          "hover:bg-[var(--color-hover-faint)] hover:text-[var(--color-text-strong)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
        )}
      >
        <Paperclip size={18} aria-hidden />
      </button>
    </>
  );

  const textarea = (
    <textarea
      ref={inputRef}
      rows={1}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onInputKeyDown}
      placeholder={t("simonBar.placeholder")}
      className={cn(
        "block min-h-[40px] min-w-0 resize-none overflow-y-auto [overflow-wrap:anywhere] rounded-[var(--radius-lg)] px-[var(--space-3)] py-[8px]",
        multiline ? "col-span-2 col-start-1 row-start-1 w-full" : "col-start-2 row-start-1 w-full",
        "bg-transparent",
        "font-(family-name:--font-display) text-md leading-[160%] tracking-[-0.01em]",
        "text-[var(--color-text-strong)] placeholder:text-[var(--color-text-faint)]",
        "focus:outline-none",
      )}
    />
  );

  const stopping = sendLocked && onStop !== undefined;
  const sendControl = (
    <button
      type="button"
      onClick={stopping ? onStop : send}
      disabled={!stopping && (sendLocked || !armed)}
      aria-label={stopping ? t("simonBar.stop") : t("simonBar.send")}
      title={stopping ? t("simonBar.stop") : uploading ? t("intelligence.attachBusy") : undefined}
      className={cn(
        "flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full",
        multiline
          ? "col-start-2 row-start-2 mr-[4px] justify-self-end"
          : "col-start-3 row-start-1 mr-[4px]",
        "bg-(--ink) text-(--paper) hover:opacity-90",
        "transition-all duration-(--duration-fast)",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
        "disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:opacity-35",
      )}
    >
      {stopping ? (
        <Square size={12} fill="currentColor" aria-hidden />
      ) : (
        <ArrowUp size={16} aria-hidden />
      )}
    </button>
  );

  return (
    <div className="relative flex w-full flex-col" {...dropTarget}>
      <span
        ref={textMeasureRef}
        aria-hidden
        className="pointer-events-none absolute invisible whitespace-pre font-(family-name:--font-display) text-md tracking-[-0.01em]"
      >
        {value || " "}
      </span>
      {shortcutRow}

      <AttachmentChips attachments={attachments} onRemove={onRemoveAttachment} />

      <div className="flex items-center gap-[var(--space-2)]">
        <div
          ref={promptRef}
          data-tour="chat"
          data-layout={multiline ? "multiline" : "single-line"}
          className={cn(
            "grid min-w-0 flex-1 items-center gap-[4px] rounded-[18px] border p-[4px]",
            multiline ? "grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-[auto_minmax(0,1fr)_auto]",
            "shadow-[0_8px_20px_rgba(26,26,26,0.035)]",
            "transition-colors duration-[var(--duration-fast)]",
            dropping ? "border-(--brand) bg-(--brand-soft)" : "border-(--line) bg-(--paper)",
          )}
        >
          {textarea}
          <div
            className={cn(
              "flex min-h-[40px] items-center gap-[var(--space-1)]",
              multiline ? "col-start-1 row-start-2" : "col-start-1 row-start-1",
            )}
          >
            {attachmentControl}
          </div>
          {sendControl}
        </div>
      </div>
    </div>
  );
}

function ComposerChips({
  label,
  onChip,
}: {
  label: (key: string) => string;
  onChip: (key: ComposerChipKey) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState<number>(COMPOSER_CHIPS.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    function measureFit() {
      if (!row || !measure || row.clientWidth === 0) return;
      const widths = Array.from(measure.children, (chip) => (chip as HTMLElement).offsetWidth);
      setShown(chipsThatFit(widths, CHIP_GAP, row.clientWidth));
    }
    measureFit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureFit);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={rowRef}
      data-tour="chips"
      className="relative mb-[8px] flex min-w-0 flex-nowrap items-center justify-center gap-[8px]"
    >
      {COMPOSER_CHIPS.slice(0, shown).map((chip) => (
        <Pill key={chip.key} onClick={() => onChip(chip.key)} className={CHIP_CLASS}>
          {label(chip.labelKey)}
        </Pill>
      ))}
      <div
        ref={measureRef}
        aria-hidden
        inert
        className="pointer-events-none invisible absolute top-0 left-0 flex gap-[8px]"
      >
        {COMPOSER_CHIPS.map((chip) => (
          <Pill key={chip.key} tabIndex={-1} className={CHIP_CLASS}>
            {label(chip.labelKey)}
          </Pill>
        ))}
      </div>
    </div>
  );
}

const CHIP_GAP = 8;

const CHIP_CLASS = "h-[32px] shrink-0 px-[16px] py-0";
